
import React from 'react';
import { Recommendation } from '@/types/assessment';
import RecommendationsHeader from './RecommendationsHeader';
import EmptyRecommendations from './EmptyRecommendations';
import CategoryRecommendationGroup from './CategoryRecommendationGroup';

interface GroupedRecommendationsSectionProps {
  recommendations: Recommendation[];
  onExport?: () => void;
  forceExpanded?: boolean;
}

const GroupedRecommendationsSection: React.FC<GroupedRecommendationsSectionProps> = ({ 
  recommendations,
  onExport,
  forceExpanded = false
}) => {
  
  const handleExportPDF = () => {
    if (onExport) {
      onExport();
      return;
    }
    
    // Fallback to text export if no onExport provided
    // Create grouped export content
    let content = "ASSESSMENT RECOMMENDATIONS\n\n";
    
    const groupedData = groupRecommendationsByCategory(recommendations);
    
    Object.entries(groupedData).forEach(([categoryName, questionGroups]) => {
      content += `${categoryName}\n${'='.repeat(categoryName.length)}\n\n`;
      
      Object.entries(questionGroups).forEach(([questionId, group]) => {
        content += `${group.questionText}\n${'-'.repeat(group.questionText.length)}\n`;
        group.recommendations.forEach(rec => {
          content += `• ${rec.text}\n`;
        });
        content += "\n";
      });
      
      content += "\n";
    });
    
    // Create a blob and download
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'assessment-recommendations.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  // Group recommendations by category, then by question
  const groupRecommendationsByCategory = (recs: Recommendation[]) => {
    const grouped: Record<string, Record<string, {
      questionText: string;
      recommendations: Recommendation[];
    }>> = {};
    
    recs.forEach(rec => {
      if (!grouped[rec.categoryName]) {
        grouped[rec.categoryName] = {};
      }
      
      if (!grouped[rec.categoryName][rec.questionId]) {
        grouped[rec.categoryName][rec.questionId] = {
          questionText: rec.questionText,
          recommendations: []
        };
      }
      
      grouped[rec.categoryName][rec.questionId].recommendations.push(rec);
    });
    
    return grouped;
  };
  
  if (!recommendations.length) {
    return <EmptyRecommendations />;
  }

  const groupedRecommendations = groupRecommendationsByCategory(recommendations);

  return (
    <div className="space-y-4">
      <RecommendationsHeader 
        recommendationsCount={recommendations.length}
        onExport={handleExportPDF}
      />
      
      <div className="space-y-4">
        {Object.entries(groupedRecommendations).map(([categoryName, questionGroups]) => (
          <CategoryRecommendationGroup
            key={categoryName}
            categoryName={categoryName}
            questionGroups={questionGroups}
            forceExpanded={forceExpanded}
          />
        ))}
      </div>
    </div>
  );
};

export default GroupedRecommendationsSection;
